/**
 * PortfolioSnapshot — Dive In KPI strip.
 *
 * Gold v2 phase 3b: every figure is a gold.portfolio_current column (the sum
 * of the position rows by construction), the same headers as the Positions
 * page. The strip formats; it does not sum. null = "—".
 */

import React from 'react';
import { isMarketOpen, getLastTradingDay, getHolidayName } from '../../utils/marketHours';
import { formatCurrency, formatSignedCurrency } from '../../utils/formatters';
import { usePortfolioKPIs } from '../../contexts/PortfolioKPIContext';

const PortfolioSnapshot: React.FC = () => {
  const { kpis } = usePortfolioKPIs();
  const s = kpis.summary;

  // Colour only for meaning: signed P&L reads gain/loss (zero stays neutral).
  const pnlClass = (v: number | null | undefined) => (v == null || v === 0 ? 'db-zero' : v > 0 ? 'db-gain' : 'db-loss');
  const marketOpen = isMarketOpen();

  // When the market is closed, Today's P&L is the last session's — relabel and
  // stamp the as-of date so the numbers read as historical, not present-tense.
  const lastTradingDay = getLastTradingDay();
  const formatAsOf = (dateStr: string): string =>
    new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const asOfLabel = lastTradingDay ? formatAsOf(lastTradingDay) : null;
  const holidayName = getHolidayName();
  const money = (v: number | null | undefined) => (v == null ? '—' : formatCurrency(v));
  const signed = (v: number | null | undefined) => (v == null ? '—' : formatSignedCurrency(v));
  const rulesFired = s?.rules_fired ?? 0;

  return (
    <div className="portfolio-snapshot">
      <div className="snapshot-header">
        <span className="snapshot-title">Portfolio</span>
        <div className="snapshot-status-group">
          {!marketOpen && asOfLabel && (
            <span className="snapshot-asof" title={holidayName ? `${holidayName} · data from ${asOfLabel}` : `Data from last close, ${asOfLabel}`}>
              as of {asOfLabel}
            </span>
          )}
          <span className={`market-status-badge ${marketOpen ? 'market-status-open' : 'market-status-closed'}`}>
            {marketOpen ? 'OPEN' : 'CLOSED'}
          </span>
        </div>
      </div>
      <div className="snapshot-kpi-grid">
        <div className="snapshot-kpi-cell" title="Market value of the shares held plus cash set aside for puts, net of what buying the options back would cost">
          <span className="snapshot-kpi-label">Portfolio value</span>
          <span className="snapshot-kpi-value">{money(s?.value)}</span>
        </div>

        <div className="snapshot-kpi-cell">
          <span className="snapshot-kpi-label">{marketOpen ? 'Today' : 'Last Close'}</span>
          <span className={`snapshot-kpi-value ${pnlClass(s?.today_pnl)}`}>{signed(s?.today_pnl)}</span>
        </div>

        <div className="snapshot-kpi-cell">
          <span className="snapshot-kpi-label">MTD</span>
          <span className={`snapshot-kpi-value ${pnlClass(s?.mtd_pnl)}`}>{signed(s?.mtd_pnl)}</span>
        </div>

        <div className="snapshot-kpi-cell">
          <span className="snapshot-kpi-label">Total P&amp;L</span>
          <span className={`snapshot-kpi-value ${pnlClass(s?.total_pnl)}`}>{signed(s?.total_pnl)}</span>
        </div>

        <div className="snapshot-kpi-cell">
          <span className="snapshot-kpi-label">Positions</span>
          <span className="snapshot-kpi-value">
            {kpis.positionCount}
            {rulesFired > 0 && (
              <span className="snapshot-alert-badge" title="Positions where one of your Discipline rules fired">
                {rulesFired} rule{rulesFired === 1 ? '' : 's'} fired
              </span>
            )}
          </span>
        </div>

        <div className="snapshot-kpi-cell">
          <span className="snapshot-kpi-label">Theta/Day</span>
          <span className="snapshot-kpi-value">{signed(s?.theta_per_day)}</span>
        </div>

        <div className="snapshot-kpi-cell" title="Remaining yield, weighted by capital committed">
          <span className="snapshot-kpi-label">Avg Yield</span>
          <span className={`snapshot-kpi-value${s?.avg_yield != null ? '' : ' db-zero'}`}>
            {s?.avg_yield != null ? `${(s.avg_yield * 100).toFixed(1)}%` : '—'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PortfolioSnapshot;
